const ENTITY_NAME_PREFIXES = ['样式样条曲线', '样条曲线', '边线', '直线', '圆弧', '圆', '椭圆', '抛物线'];
const EDGE_LABEL_PREFIX = '边线';
const NUMERIC_SUFFIX_PATTERN = /^\d+$/;

/**
 * 解开标签上已有的“在...上”修饰。
 *
 * @param value 原始标签。
 * @returns 去修饰后的标签。
 */
function unwrapEdgeLabelDecorations(value: string): string {
	const trimmedValue = value.trim();
	if (trimmedValue.startsWith('在') && trimmedValue.endsWith('上') && trimmedValue.length > 2) {
		return trimmedValue.slice(1, -1).trim();
	}

	return trimmedValue;
}

/**
 * 判断持久化标签是否已经是稳定边线标签。
 *
 * @param value 候选标签。
 * @returns 是否为稳定标签。
 */
function isStablePersistedSourceDisplayLabel(value: string | null | undefined): boolean {
	const normalizedValue = unwrapEdgeLabelDecorations(value ?? '');
	return normalizedValue.startsWith(EDGE_LABEL_PREFIX);
}

/**
 * 解析编号后缀。
 *
 * @param suffix 原始后缀。
 * @returns 可用编号；无法解析时返回 null。
 */
function parseNumericSuffix(suffix: string): number | null {
	if (!NUMERIC_SUFFIX_PATTERN.test(suffix)) {
		return null;
	}

	const numericValue = Number(suffix);
	return Number.isSafeInteger(numericValue) ? numericValue : null;
}

/**
 * 规范化运行时来源实体名。
 *
 * 设计说明：
 * 1. SketchLineObject / SketchCircleObject 等运行时 name 使用内部 0 基计数，例如 `圆0`；
 * 2. Convert Entities 面板和几何关系列表需要展示 SolidWorks 风格的 1 基 `边线N`；
 * 3. 因此运行时实体名遇到纯数字后缀时必须 +1，再统一改写为 `边线N`。
 *
 * @param value 候选运行时名称。
 * @returns 规范化后的标签；无效时返回 null。
 */
function normalizeRuntimeSourceDisplayLabelCandidate(value: string | null | undefined): string | null {
	const rawName = unwrapEdgeLabelDecorations(value ?? '');
	if (!rawName) {
		return null;
	}

	for (const prefix of ENTITY_NAME_PREFIXES) {
		if (rawName.startsWith(prefix)) {
			const suffix = rawName.slice(prefix.length);
			if (!suffix) {
				return EDGE_LABEL_PREFIX;
			}

			const numericSuffix = parseNumericSuffix(suffix);
			if (numericSuffix !== null) {
				return `${EDGE_LABEL_PREFIX}${numericSuffix + 1}`;
			}

			return `${EDGE_LABEL_PREFIX}${suffix}`;
		}
	}

	return rawName;
}

/**
 * 规范化已经稳定化的来源标签。
 *
 * 设计说明：
 * 1. 会话选择与引用记录保存的是用户可见标签，通常已经是 1 基 `边线N`；
 * 2. 这类标签不能再按运行时对象名 +1，否则 `边线2` 会错误变成 `边线3`；
 * 3. 仅对历史遗留的 `边线0` 做兼容提升，避免旧记录继续显示 0 基编号。
 *
 * @param value 候选稳定标签。
 * @returns 规范化后的标签；无效时返回 null。
 */
function normalizeStableSourceDisplayLabelCandidate(value: string | null | undefined): string | null {
	const rawName = unwrapEdgeLabelDecorations(value ?? '');
	if (!rawName) {
		return null;
	}

	if (!rawName.startsWith(EDGE_LABEL_PREFIX)) {
		return rawName;
	}

	const suffix = rawName.slice(EDGE_LABEL_PREFIX.length);
	if (!suffix) {
		return EDGE_LABEL_PREFIX;
	}

	const numericSuffix = parseNumericSuffix(suffix);
	if (numericSuffix === 0) {
		return `${EDGE_LABEL_PREFIX}1`;
	}

	return rawName;
}

/**
 * 解析来源显示标签。
 *
 * @param sourceEntityName 来源实体名。
 * @returns 可展示标签。
 */
export function resolveSourceDisplayLabel(sourceEntityName: string): string {
	return normalizeRuntimeSourceDisplayLabelCandidate(sourceEntityName) ?? EDGE_LABEL_PREFIX;
}

/**
 * 解析稳定来源显示标签。
 *
 * @param sourceDisplayLabel 会话选择或引用记录中的来源显示标签。
 * @returns 可展示标签。
 */
export function resolveStableSourceDisplayLabel(sourceDisplayLabel: string): string {
	return normalizeStableSourceDisplayLabelCandidate(sourceDisplayLabel) ?? EDGE_LABEL_PREFIX;
}

/**
 * 解析优先来源显示标签。
 *
 * @param input 运行时标签与持久化标签。
 * @returns 优先使用后的来源标签。
 */
export function resolvePreferredSourceDisplayLabel(input: {
	runtimeSourceName?: string | null;
	persistedSourceDisplayLabel?: string | null;
}): string {
	if (isStablePersistedSourceDisplayLabel(input.persistedSourceDisplayLabel)) {
		return normalizeStableSourceDisplayLabelCandidate(input.persistedSourceDisplayLabel) ?? EDGE_LABEL_PREFIX;
	}

	return normalizeRuntimeSourceDisplayLabelCandidate(input.runtimeSourceName)
		?? normalizeStableSourceDisplayLabelCandidate(input.persistedSourceDisplayLabel)
		?? EDGE_LABEL_PREFIX;
}

/**
 * 解析“在边线上”形式的显示标签。
 *
 * @param sourceEntityName 来源实体名。
 * @returns 关系显示标签。
 */
export function resolveEdgeLabel(sourceEntityName: string): string {
	return `在${resolveSourceDisplayLabel(sourceEntityName)}上`;
}

/**
 * 解析优先关系显示标签。
 *
 * @param input 运行时标签与持久化标签。
 * @returns 关系显示标签。
 */
export function resolvePreferredEdgeLabel(input: {
	runtimeSourceName?: string | null;
	persistedSourceDisplayLabel?: string | null;
}): string {
	return `在${resolvePreferredSourceDisplayLabel(input)}上`;
}
